import { useEffect, useMemo } from 'react';
import { Add } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';
import { UserPlan, useBankConnectionsQuery, useUserQuery } from '../../api/gql';
import { FullSizeLoader } from '../../components/FullSizeLoader';
import { PageHeader } from '../../components/PageHeader';
import { PageContent, PageLayout } from '../../layout';
import { BankConnectionForTrial } from '../../modules/bank-connection/BankConnectionForTrial';
import { BankConnections } from '../../modules/bank-connection/BankConnections';
import { HowToUseBankConnection } from '../../modules/bank-connection/HowToUseBankConnection';
import { HowToUseBankConnectionInfoButton } from '../../modules/bank-connection/HowToUseBankConnectionInfoButton';
import { notify } from '../../utils/notification';
import { useUrlParam } from '../../utils/urlParams';

export const BankConnection = () => {
  const { t } = useTranslation('bank-connection');
  const { data, isLoading } = useUserQuery();

  const isTrial = useMemo(() => data?.user?.plan === UserPlan.Trial, [data]);

  const { data: bankConnections, isLoading: isBankConnectionsLoading } = useBankConnectionsQuery();

  const hasBankConnections = useMemo(() => {
    return bankConnections?.bankConnections?.length;
  }, [bankConnections]);

  const [getParam, , clear] = useUrlParam('clientId');

  // When there is url searchParam, notify user on successful bank connection
  useEffect(() => {
    if (getParam()) {
      notify.positive({ message: t('bankConnection.mutation.success') });
      clear();
    }
  }, [clear, getParam, t]);

  return (
    <PageLayout>
      <PageHeader
        title={t('bankConnection.title')}
        titleSlot={hasBankConnections ? <HowToUseBankConnectionInfoButton /> : null}
        backRouteTo={null}
        action={
          <LoadingButton
            component={Link}
            to={'/bank-connection/create'}
            loading={isLoading}
            size={'large'}
            variant={'contained'}
            startIcon={<Add />}
            disabled={isTrial}
          >
            {t('bankConnection.connectBank')}
          </LoadingButton>
        }
      />
      <PageContent
        sx={{
          flexDirection: 'column',
          gap: 4,
          justifyContent: 'flex-start',
          mt: 4,
        }}
      >
        {isBankConnectionsLoading ? <FullSizeLoader /> : null}
        {hasBankConnections ? <BankConnections bankConnections={bankConnections?.bankConnections} /> : null}
        {!hasBankConnections ? <HowToUseBankConnection /> : null}
        {isTrial ? <BankConnectionForTrial /> : null}
      </PageContent>
      {/*  Outlet for modal pages */}
      <Outlet />
    </PageLayout>
  );
};
